import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFreshness } from "@/context/FreshnessContext";

// Small "Data as of …" trust line — used on drill-down lists and leaderboards so
// nobody mistakes stale numbers for live ones. Colors follow the staleness level.
const FRESHNESS_TONES = {
  crimson: "text-[hsl(var(--cx-crimson))]",
  amber: "text-[hsl(var(--status-amber))]",
};

export const DataAsOf = ({ className = "" }) => {
  const { freshness } = useFreshness();
  if (!freshness?.has_data) return null;
  const tone = FRESHNESS_TONES[freshness.level] || "text-muted-foreground";
  return (
    <span className={cn("text-[0.65rem] tabular whitespace-nowrap", tone, className)} data-testid="data-as-of-line">
      Data as of {freshness.at_fmt}
    </span>
  );
};

export const RagBadge = ({ rag, days }) => {
  const styles = {
    Red: "bg-[hsl(var(--status-red)/0.1)] text-[hsl(var(--status-red))] border-[hsl(var(--status-red)/0.3)]",
    Amber: "bg-[hsl(var(--status-amber)/0.12)] text-[hsl(var(--status-amber))] border-[hsl(var(--status-amber)/0.3)]",
    Green: "bg-[hsl(var(--status-green)/0.1)] text-[hsl(var(--status-green))] border-[hsl(var(--status-green)/0.3)]",
  };
  return (
    <Badge variant="outline" className={cn("gap-1 font-medium tabular", styles[rag] || "")} data-testid="rag-badge">
      {rag}
      {days !== undefined && days !== null ? ` · ${days}d` : ""}
    </Badge>
  );
};

export const StatusBadge = ({ status }) => {
  const styles = {
    Open: "bg-[hsl(var(--cx-navy)/0.08)] text-[hsl(var(--cx-navy))] border-[hsl(var(--cx-navy)/0.2)]",
    "In Progress": "bg-[hsl(var(--status-amber)/0.1)] text-[hsl(var(--status-amber))] border-[hsl(var(--status-amber)/0.25)]",
    Resolved: "bg-[hsl(var(--status-green)/0.1)] text-[hsl(var(--status-green))] border-[hsl(var(--status-green)/0.25)]",
    Closed: "bg-[hsl(var(--cx-slate)/0.1)] text-[hsl(var(--cx-slate))] border-[hsl(var(--cx-slate)/0.25)]",
    Reopened: "bg-[hsl(var(--status-red)/0.1)] text-[hsl(var(--status-red))] border-[hsl(var(--status-red)/0.3)]",
  };
  return (
    <Badge variant="outline" className={cn("font-medium whitespace-nowrap", styles[status] || "")} data-testid="status-badge">
      {status}
    </Badge>
  );
};

export const DelayBadge = ({ delayClass }) => {
  // Strict VNA rule: delay_class is either "oem" or null — nothing else exists.
  if (delayClass !== "oem") return null;
  return (
    <Badge className="bg-[hsl(var(--cx-navy))] text-white border-transparent font-medium whitespace-nowrap hover:bg-[hsl(var(--cx-navy))]" data-testid="delay-badge-oem">
      OEM Delay
    </Badge>
  );
};

export const EscalationBadge = ({ level, showLabel }) => {
  if (!level) return null;
  const styles = {
    5: "bg-[hsl(var(--cx-crimson))] text-white border-transparent",
    4: "bg-[hsl(var(--cx-crimson)/0.1)] text-[hsl(var(--cx-crimson))] border-[hsl(var(--cx-crimson)/0.35)]",
    3: "bg-[hsl(var(--status-amber)/0.12)] text-[hsl(var(--status-amber))] border-[hsl(var(--status-amber)/0.35)]",
    2: "bg-[hsl(var(--cx-navy)/0.08)] text-[hsl(var(--cx-navy))] border-[hsl(var(--cx-navy)/0.25)]",
    1: "bg-[hsl(var(--cx-slate)/0.1)] text-[hsl(var(--cx-slate))] border-[hsl(var(--cx-slate)/0.3)]",
  };
  const labels = { 5: "MD", 4: "VP", 3: "Cluster Head", 2: "Outlet Mgr", 1: "CCM/CRM" };
  return (
    <Badge variant="outline" className={cn("font-semibold tabular whitespace-nowrap", styles[level] || "")}
      data-testid={`escalation-badge-l${level}`}>
      L{level}{showLabel ? ` · ${labels[level]}` : ""}
    </Badge>
  );
};

export const TrendArrow = ({ trend }) => {
  if (trend === "up")
    return <ArrowUpRight size={16} className="text-[hsl(var(--status-green))]" aria-label="improving" />;
  if (trend === "down")
    return <ArrowDownRight size={16} className="text-[hsl(var(--status-red))]" aria-label="declining" />;
  return <Minus size={16} className="text-muted-foreground" aria-label="flat" />;
};

export const KpiCard = ({ label, value, sub, accent, testId, onClick, pulse }) => (
  <Card
    data-testid={testId}
    onClick={onClick}
    className={cn(
      "p-4 flex flex-col gap-1 transition-shadow duration-150 hover:shadow-md",
      onClick && "cursor-pointer",
      accent && "border-t-2 border-t-[hsl(var(--cx-gold))]"
    )}
  >
    <div className="flex items-center gap-2">
      <span className="text-[0.65rem] uppercase tracking-[0.12em] text-muted-foreground font-medium">{label}</span>
      {pulse && <span className="red-dot inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--status-red))]" />}
    </div>
    <div className="kpi-value text-2xl sm:text-3xl text-[hsl(var(--cx-ink))]">{value}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </Card>
);

export const FunctionToggle = ({ value, onChange, locked }) => {
  if (locked)
    return (
      <Badge variant="outline" className="h-8 px-3 text-[hsl(var(--cx-navy))] border-[hsl(var(--cx-gold)/0.5)] bg-[hsl(var(--accent))]" data-testid="function-scope-locked">
        {value} scope
      </Badge>
    );
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => v && onChange(v)}
      className="border rounded-md p-0.5 bg-[hsl(var(--cx-surface-2))]"
      data-testid="topbar-function-toggle"
    >
      {["Both", "Sales", "Service"].map((f) => (
        <ToggleGroupItem
          key={f}
          value={f}
          data-testid={`function-toggle-${f.toLowerCase()}`}
          className="h-7 px-3 text-xs data-[state=on]:bg-[hsl(var(--cx-navy))] data-[state=on]:text-white rounded"
        >
          {f}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};

export const EmptyState = ({ title, subtitle }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-state">
    <p className="font-serif text-lg text-[hsl(var(--cx-ink))]">{title}</p>
    {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
  </div>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
    <div>
      <h1 className="font-serif text-2xl sm:text-3xl tracking-tight text-[hsl(var(--cx-ink))]">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
    {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
  </div>
);
